import dayjs from 'dayjs';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';

const VITE_SAFALUTILITIES_APPID = 'safalsubscriptionstadley';
const VITE_SAFALUTILITIES_BACKEND_URL = 'https://api.dev.safalutilities.com';

const URL = `${VITE_SAFALUTILITIES_BACKEND_URL}/api/notification-orchestrator/hemantcompanywzg6lw/${VITE_SAFALUTILITIES_APPID}`;

const getAllInterestAPI = async (query) => {
  try {
    const qs = query ? queryString.stringify(query, { skipNull: true, skipEmptyString: true }) : '';
    const res = await fetch(`${URL}/interests?${qs}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const resData = await res.json();
    return resData?.data || [];
  } catch (err) {
    console.error(err.message);
    throw err;
  }
};

const getMyInterestAPI = async (query) => {
  try {
    const qs = query ? queryString.stringify(query, { skipNull: true, skipEmptyString: true }) : '';
    const res = await fetch(`${URL}/data-collection?${qs}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const resData = await res.json();
    return resData?.data;
  } catch (err) {
    console.error(err.message);
    throw err;
  }
};

const postDataCollectionAPI = async (data) => {
  try {
    const res = await fetch(`${URL}/data-collection`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const resData = await res.json();
    return resData;
  } catch (err) {
    console.error(err.message);
    throw err;
  }
};

const InterestDialog = ({ openSt, onCloseX }) => {
  const [openState, setOpenState] = useState(openSt || 'CLOSE');
  const { user } = useSelector((state) => state.login);
  const [allInterest, setAllInterest] = useState([]);
  const [myInterest, setMyInterest] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [myInterestState, setMyInterestState] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [allInterestData, myInterestData] = await Promise.all([
        getAllInterestAPI(),
        getMyInterestAPI({ userId: user?._id }),
      ]);
      setAllInterest(allInterestData);
      setMyInterest(myInterestData?.interests || []);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [user?._id]);

  useEffect(() => {
    fetchData();
  }, [user?._id, fetchData]);

  const onClose = () => {
    setOpenState('CLOSE');
    onCloseX?.();
  };

  useEffect(() => {
    if (isLoading) return;
    setMyInterestState(myInterest);
    const hasVisited = localStorage.getItem('hasVisitedInterestDialog');
    const isSameDay = hasVisited ? dayjs().isSame(dayjs(hasVisited), 'day') : false;
    if (isSameDay) return;

    if (allInterest.length > 0 && myInterest?.length === 0) {
      localStorage.setItem('hasVisitedInterestDialog', new Date().toISOString());
      setOpenState('OPEN');
    } else {
      setMyInterestState(myInterest);
    }
  }, [allInterest, myInterest, isLoading]);

  const onSave = async () => {
    try {
      await postDataCollectionAPI({
        userId: user?._id || '',
        interests: myInterestState,
      });
      fetchData();
      toast.success('Interests saved successfully!');
      onClose();
    } catch {
      toast.error('Failed to save interests. Please try again.');
    }
  };

  if (isLoading) return null;

  return (
    <Dialog open={openState === 'OPEN'} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Interests (Optional)</DialogTitle>

      <DialogContent dividers sx={{ maxHeight: 500, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DialogContentText sx={{ mb: 1 }}>
          We noticed you haven't selected any interests yet. Please select your interests to get personalized content
          and recommendations.
        </DialogContentText>

        {allInterest.map((interest) => (
          <FormControlLabel
            key={interest.value}
            label={interest.label}
            control={
              <Checkbox
                checked={myInterestState?.includes(interest.value)}
                onChange={(e) => {
                  setMyInterestState((prev) =>
                    e.target.checked ? [...(prev || []), interest.value] : prev?.filter((i) => i !== interest.value)
                  );
                }}
              />
            }
          />
        ))}
      </DialogContent>

      <DialogActions sx={{ gap: 1 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {myInterestState.length !== 0 ? (
          <Button variant="contained" onClick={onSave}>
            Save
          </Button>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Remind Me Later
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InterestDialog;
